import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  LinearProgress,
  IconButton
} from '@mui/material';
import {
  Description as FileIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  Refresh as RetryIcon
} from '@mui/icons-material';
import { formatFileSize } from '../../utils/formatters';
const FileUploadItem = ({ 
  file, 
  status = 'success', 
  progress = 100, 
  uploadSpeed, 
  onRetry, 
  onMoreOptions,
  errorMessage
}) => {
  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return '#10b981';
      case 'error':
        return '#ef4444';
      case 'uploading':
        return '#3b82f6';
      case 'idle':
        return 'transparent';
      default:
        return '#6b7280';
    }
  };
  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <SuccessIcon sx={{ fontSize: 16, color: getStatusColor() }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 16, color: getStatusColor() }} />;
      case 'idle':
        return null;
      default:
        return null;
    }
  };
  const getStatusText = () => {
    switch (status) {
      case 'success':
        return 'Successful';
      case 'error':
        if (errorMessage) {
          return errorMessage;
        }
        return 'File format not supported. Supported formats: PDF, DOC, DOCX, XLS, XLSX, TXT, CSV, XML, JSON, JPG, PNG, GIF, WEBP';
      case 'uploading':
        return `Uploading.. ${progress}%`;
      case 'idle':
        return '';
      default:
        return 'Unknown';
    }
  };
  return (
    <Box sx={{ 
      p: 2, 
      border: '1px solid #e5e7eb', 
      borderRadius: 2, 
      backgroundColor: 'white',
      mb: 1
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* File Icon */}
        <FileIcon sx={{ 
          color: '#f59e0b', 
          fontSize: 24, 
          mt: 0.5 
        }} />
        {/* File Details */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ 
            fontWeight: 500, 
            color: '#374151',
            mb: 0.5
          }}>
            {file.name}
          </Typography>
          <Typography variant="caption" sx={{ 
            color: '#6b7280',
            display: 'block',
            mb: 1
          }}>
            Size - {formatFileSize(file.size)}
          </Typography>
          {/* Progress Bar */}
          {status === 'uploading' && (
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                mb: 1,
                height: 4,
                borderRadius: 2,
                backgroundColor: '#f3f4f6',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getStatusColor(),
                  borderRadius: 2
                }
              }} 
            />
          )}
          {/* Status and Speed */}
          {status !== 'idle' && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getStatusIcon()}
                <Typography variant="caption" sx={{ 
                  color: getStatusColor(),
                  fontWeight: 500
                }}>
                  {getStatusText()}
                </Typography>
              </Box>
              {status === 'uploading' && uploadSpeed && (
                <Typography variant="caption" sx={{ 
                  color: '#6b7280'
                }}>
                  {uploadSpeed}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {status === 'error' && onRetry && (
            <IconButton
              size="small"
              onClick={onRetry}
              sx={{
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                '&:hover': {
                  backgroundColor: '#f9fafb'
                }
              }}
            >
              <RetryIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={onMoreOptions}
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
                color: '#374151'
              }
            }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
FileUploadItem.propTypes = {
  file: PropTypes.shape({
    name: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired
  }).isRequired,
  status: PropTypes.oneOf(['success', 'error', 'uploading']),
  progress: PropTypes.number,
  uploadSpeed: PropTypes.string,
  onRetry: PropTypes.func,
  onMoreOptions: PropTypes.func,
  errorMessage: PropTypes.string
};
export default FileUploadItem; 